// ----------------------------------------------------------------------------
// Copyright (c) Ben Coleman, 2021
// Licensed under the MIT License.
//
// Drop in MSAL.js 2.x service wrapper & helper for SPAs
//   v2.1.0 - Ben Coleman 2019
//   Updated 2021 - Switched to @azure/msal-browser
// ----------------------------------------------------------------------------

import * as msal from "@azure/msal-browser";

// MSAL object used for signing in users with MS identity platform
let msalApp;

// Force users to interactively sign in at least every 30 days. We stamp the
// time of each interactive login and treat the session as signed-out once that
// stamp is older than this window, which makes the app show the login screen
// again on the next load.
const SESSION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const LAST_LOGIN_KEY = "itfma_last_login";

export default {
  //
  // Configure with clientId or empty string/null to set in "demo" mode
  //
  async configure(clientId) {
    // Can only call configure once
    if (msalApp) {
      return;
    }

    // Can't configure if clientId blank/null/undefined
    if (!clientId) {
      return;
    }

    const config = {
      auth: {
        clientId: clientId,
        redirectUri: window.location.origin,
        authority:
          "https://login.microsoftonline.com/ff5a36c3-1fe4-4f3f-8662-de463e12109d",
      },
      cache: {
        cacheLocation: "localStorage",
      },
    };
    console.log("### Azure AD sign-in: enabled\n", config);

    // Create our shared/static MSAL app object
    msalApp = new msal.PublicClientApplication(config);
  },

  //
  // Return the configured client id
  //
  clientId() {
    if (!msalApp) {
      return null;
    }

    return msalApp.config.auth.clientId;
  },

  //
  // Login a user with a popup
  //
  async login(scopes = ["user.read", "openid", "profile", "email"]) {
    if (!msalApp) {
      return;
    }

    console.log("msal");

    //const LOGIN_SCOPES = ['user.read', 'openid', 'profile', 'email']
    await msalApp.loginPopup({
      scopes,
      prompt: "select_account",
    });

    // Stamp this interactive sign-in for the 30-day re-login policy.
    this.recordLogin();
  },

  //
  // Record the time of the latest interactive sign-in.
  //
  recordLogin() {
    try {
      localStorage.setItem(LAST_LOGIN_KEY, Date.now().toString());
    } catch (e) {
      // localStorage unavailable (private mode / disabled) - nothing to do
    }
  },

  //
  // True when the user has not interactively signed in within the last 30 days
  // (or when we have no record of a sign-in). Used to force a fresh login.
  //
  isSessionExpired() {
    const last = parseInt(localStorage.getItem(LAST_LOGIN_KEY), 10);
    if (!last || Number.isNaN(last)) {
      return true;
    }
    return Date.now() - last > SESSION_MAX_AGE_MS;
  },

  //
  // One-time migration for sessions that predate the 30-day policy: if the user
  // already has a cached account but no recorded sign-in time, treat this load
  // as the start of their window instead of forcing an immediate re-login.
  // Brand-new users (no cached account) are left alone so they still must log in.
  //
  seedExistingSession() {
    if (!msalApp) {
      return;
    }
    if (localStorage.getItem(LAST_LOGIN_KEY)) {
      return;
    }
    if (msalApp.getAllAccounts().length > 0) {
      this.recordLogin();
    }
  },

  //
  // Logout any stored user
  //
  logout() {
    if (!msalApp) {
      return;
    }

    msalApp.logoutPopup();
  },

  //
  // Call to get user, probably cached and stored locally by MSAL
  //
  user() {
    if (!msalApp) {
      console.log("returning null");
      return null;
    }

    // Enforce the 30-day re-login policy: an expired session is treated as
    // signed-out so the app shows the login screen and forces a fresh sign-in.
    if (this.isSessionExpired()) {
      return null;
    }

    const currentAccounts = msalApp.getAllAccounts();
    if (!currentAccounts || currentAccounts.length === 0) {
      // No user signed in
      console.log("length === 0 , returning null");
      return null;
    } else if (currentAccounts.length > 1) {
      // more than one signe din user, return the @seatow.com user
      for (let i = 0; i < currentAccounts.length; i++) {
        if (currentAccounts[i].username.toLowerCase().endsWith("@seatow.com")) {
          return currentAccounts[i];
        }
      }

      // If no @seatow.com user found, return the first one
      return currentAccounts[0];
    } else {
      return currentAccounts[0];
    }
  },

  //
  // Call through to acquireTokenSilent or acquireTokenPopup
  //
  async acquireToken(scopes = ["user.read"]) {
    if (!msalApp) {
      return null;
    }

    // Set scopes for token request
    const accessTokenRequest = {
      scopes,
      account: this.user(),
    };

    let tokenResp;
    try {
      // 1. Try to acquire token silently
      tokenResp = await msalApp.acquireTokenSilent(accessTokenRequest);
      console.log("### MSAL acquireTokenSilent was successful");
    } catch (err) {
      // 2. Silent process might have failed so try via popup
      tokenResp = await msalApp.acquireTokenPopup(accessTokenRequest);
      console.log("### MSAL acquireTokenPopup was successful");
    }

    // Just in case check, probably never triggers
    if (!tokenResp.accessToken) {
      throw new Error("### accessToken not found in response, that's bad");
    }

    return tokenResp.accessToken;
  },

  //
  // Clear any stored/cached user
  //
  clearLocal() {
    localStorage.removeItem(LAST_LOGIN_KEY);
    if (msalApp) {
      for (let entry of Object.entries(localStorage)) {
        let key = entry[0];
        if (key.includes("login.windows")) {
          localStorage.removeItem(key);
        }
      }
    }
  },

  //
  // Check if we have been setup & configured
  //
  isConfigured() {
    return msalApp != null;
  },
};
